import { cn } from '@skin-seoul/design-system';
import type { ReactNode, ElementType } from 'react';
import { Convert } from '@skin-seoul/utils';
import { LucideIcon } from '../../components/LucideIcon';

type Props = {
  href?: string | (() => void);
  theme?: 'dark' | 'light';
  title: ReactNode;
  subtitle?: ReactNode;
  linkComponent?: ElementType;
  className?: string;
};

const SectionHeader = ({ theme = 'light', title, subtitle, href, linkComponent, className }: Props) => {
  const isDark = theme === 'dark';
  const LinkComponent = linkComponent;
  return (
    <div className={cn('flex flex-col w-full gap-2', className)}>
      <div className="flex items-center gap-4 w-full tablet:w-fit">
        <Convert
          value={href}
          formatter={(href: string | (() => void)) => {
            const isFunction = typeof href === 'function';

            if (isFunction) {
              return (
                <button
                  onClick={href}
                  className={cn(
                    'flex gap-6 w-full flex-row items-center justify-between mobile:justify-start cursor-pointer bg-transparent border-none',
                    isDark ? 'text-generic-0' : 'text-generic-100',
                    'hover:[&_h2]:text-primary-400 hover:[&_svg]:text-primary-400',
                    isDark
                      ? 'active:[&_h2]:text-primary-200 active:[&_svg]:text-primary-200'
                      : 'active:[&_h2]:text-primary-600 active:[&_svg]:text-primary-600',
                  )}
                >
                  <>
                    <h2 className="text-heading-h5 font-normal">{title}</h2>
                    <LucideIcon name="ChevronRight" size={28} />
                  </>
                </button>
              );
            }

            if (LinkComponent) {
              return (
                <LinkComponent
                  href={href as string}
                  className={cn(
                    'flex gap-6 w-full flex-row items-center justify-between mobile:justify-start',
                    isDark ? 'text-generic-0' : 'text-generic-100',
                    'hover:[&_h2]:text-primary-400 hover:[&_svg]:text-primary-400',
                    isDark
                      ? 'active:[&_h2]:text-primary-200 active:[&_svg]:text-primary-200'
                      : 'active:[&_h2]:text-primary-600 active:[&_svg]:text-primary-600',
                  )}
                >
                  <>
                    <h2 className="text-heading-h5 font-normal">{title}</h2>
                    <LucideIcon name="ChevronRight" size={28} />
                  </>
                </LinkComponent>
              );
            }

            return (
              <a
                href={href as string}
                className={cn(
                  'flex gap-6 w-full flex-row items-center justify-between mobile:justify-start no-underline hover:no-underline',
                  isDark ? 'text-generic-0' : 'text-generic-100',
                  'hover:[&_h2]:text-primary-400 hover:[&_svg]:text-primary-400',
                  isDark
                    ? 'active:[&_h2]:text-primary-200 active:[&_svg]:text-primary-200'
                    : 'active:[&_h2]:text-primary-600 active:[&_svg]:text-primary-600',
                )}
              >
                <>
                  <h2 className="text-heading-h5 font-normal">{title}</h2>
                  <LucideIcon name="ChevronRight" size={28} />
                </>
              </a>
            );
          }}
          defaultValue={
            <h2 className={cn('text-heading-h5 font-normal', isDark ? 'text-generic-0' : 'text-generic-100')}>
              {title}
            </h2>
          }
        />
      </div>

      <p className={cn('text-paragraph-medium font-normal', isDark ? 'text-neutral-100' : 'text-neutral-600')}>
        {subtitle}
      </p>
    </div>
  );
};

export default SectionHeader;
